import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Download, Save, CheckCircle2 } from "lucide-react";
import { reportsApi, downloadBlob } from "@/api/reports";
import type { GeneratedReportDetailDto } from "@/api/reports";
import { Badge, Button } from "@/design-system/components/ui";
import { Textarea } from "@/components/ui/Select";
import { PageHeader } from "@/components/layout/PageHeader";
import { useDateFormatter } from "@/utils/datetime";

export function ReportViewerPage() {
  const { id = "" } = useParams();
  const [body, setBody] = useState<string>("");
  const [status, setStatus] = useState<string>("");
  const { dateTime } = useDateFormatter();

  const report = useQuery<GeneratedReportDetailDto>({
    queryKey: ["report", id],
    queryFn: () => reportsApi.get(id),
    enabled: !!id,
  });

  useEffect(() => {
    if (report.data) setBody(report.data.bodyText);
  }, [report.data]);

  const saveMutation = useMutation({
    mutationFn: (bodyText: string) => reportsApi.updateBody(id, bodyText),
    onSuccess: () => setStatus("Saved"),
  });

  const finalizeMutation = useMutation({
    mutationFn: async () => {
      const blob = await reportsApi.finalize(id, body);
      downloadBlob(blob, `${(report.data?.title ?? "report").replace(/[^a-z0-9-_]/gi, "-")}.docx`);
      setStatus("Finalized & downloaded");
      await report.refetch();
    },
  });

  const exportMutation = useMutation({
    mutationFn: async () => {
      const blob = await reportsApi.exportDocx(id);
      downloadBlob(blob, `${(report.data?.title ?? "report").replace(/[^a-z0-9-_]/gi, "-")}.docx`);
    },
  });

  if (report.isLoading || !report.data) {
    return (
      <>
        <PageHeader eyebrow="Reports" title="Loading…" />
      </>
    );
  }
  const r = report.data;

  return (
    <>
      <PageHeader
        eyebrow="Report"
        title={r.title}
        description={`From template '${r.promptTemplateName}' v${r.promptTemplateVersion}`}
        actions={
          <>
            <Button variant="secondary" onClick={() => saveMutation.mutate(body)} loading={saveMutation.isPending}>
              <Save size={16} aria-hidden /> Save draft
            </Button>
            <Button onClick={() => finalizeMutation.mutate()} loading={finalizeMutation.isPending}>
              <CheckCircle2 size={16} aria-hidden /> Finalize & download
            </Button>
            <Button variant="tertiary" onClick={() => exportMutation.mutate()} loading={exportMutation.isPending}>
              <Download size={16} aria-hidden /> Export DOCX
            </Button>
          </>
        }
      />

      <div className="flex items-center gap-space-2 mb-space-3">
        <Badge status="info">{r.outputFormat}</Badge>
        {r.isFinalized ? <Badge status="finalized">Finalized</Badge> : <Badge status="draft">Draft</Badge>}
        <span className="text-caption text-gray-mid">Created {dateTime(r.createdAt)}</span>
        {status && <span className="text-caption text-status-success">· {status}</span>}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-space-4">
        <div className="bg-white-pure border border-border-light rounded-lg p-space-4">
          <div className="font-display text-caption font-semibold uppercase tracking-wider text-onyx mb-space-2">
            Editor
          </div>
          <Textarea
            rows={28}
            value={body}
            onChange={(e) => setBody(e.target.value)}
            className="font-mono text-caption"
          />
        </div>
        <div className="bg-white-warm border border-border-light rounded-lg p-space-4">
          <div className="font-display text-caption font-semibold uppercase tracking-wider text-onyx mb-space-2">
            Preview
          </div>
          <article className="prose-like max-w-reading text-onyx">
            {body.split("\n").map((line, idx) => {
              if (line.startsWith("# ")) {
                return <h1 key={idx} className="font-display font-bold text-h2 tracking-tight mt-space-3 mb-space-2">{line.slice(2)}</h1>;
              }
              if (line.startsWith("## ")) {
                return <h2 key={idx} className="font-display font-bold text-h3 text-gold-shadow mt-space-3 mb-space-2">{line.slice(3)}</h2>;
              }
              if (line.trim() === "") return <div key={idx} className="h-space-2" />;
              return <p key={idx} className="text-body leading-relaxed mb-space-2">{line}</p>;
            })}
          </article>
        </div>
      </div>
    </>
  );
}
